import React, { useState, useMemo, useEffect } from "react";
import greater from "../../images/greater.svg";
import { useGetUserBetsMutation } from "../../store/apis";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/auth-slice";
import selectdropdownsvg from "../../images/selectdropdownsvg.svg";
import { CURRENCY } from "../../constants";

// Status styling with your theming
const STATUS_STYLES = {
  won: {
    bg: "bg-[#11A306]/20",
    text: "text-[#11A306]",
    border: "border-[#11A306]",
  },
  lost: {
    bg: "bg-[#ec3737]/20",
    text: "text-[#ec3737]",
    border: "border-[#ec3737]",
  },
  pending: {
    bg: "bg-yellow/20",
    text: "text-yellow",
    border: "border-yellow",
  },
  cancelled: {
    bg: "bg-doubleextralightwhite/20",
    text: "text-doubleextralightwhite",
    border: "border-doubleextralightwhite",
  },
};

const BetHistory = () => {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [getUserBets, { isLoading }] = useGetUserBetsMutation();
  const [betData, setBetData] = useState({ count: 0, bets: [] });
  const user = useSelector(selectUser);
  const [expandedBets, setExpandedBets] = useState({});
  const itemsPerPage = 8;

  // Fetch user bets on component mount and when filters change
  useEffect(() => {
    const fetchUserBets = async () => {
      try {
        const response = await getUserBets({
          customer_id: user.id,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          bet_type: selectedType !== "all" ? selectedType : undefined,
        }).unwrap();

        setBetData(response);
      } catch (error) {
        console.error("Failed to fetch bet history:", error);
      }
    };

    fetchUserBets();
  }, [getUserBets, dateFrom, dateTo, selectedType, user.id]);

  // Group bets by bet_id (betslip)
  const groupedBets = useMemo(() => {
    const groups = {};

    betData.bets.forEach((bet) => {
      if (!groups[bet.bet_id]) {
        groups[bet.bet_id] = {
          bet_id: bet.bet_id,
          createdAt: new Date(bet.createdAt),
          formattedDate: formatDate(bet.createdAt),
          bets: [],
          totalStake: 0,
          totalPotentialPayout: 0,
          status: bet.status,
        };
      }

      groups[bet.bet_id].bets.push(bet);
      groups[bet.bet_id].totalStake += parseFloat(bet.stake_amount) || 0;
      groups[bet.bet_id].totalPotentialPayout +=
        parseFloat(bet.potential_payout) || 0;

      // Status logic
      if (bet.status === "lost") {
        groups[bet.bet_id].status = "lost";
      } else if (
        bet.status === "won" &&
        groups[bet.bet_id].status !== "lost" &&
        groups[bet.bet_id].status !== "pending"
      ) {
        groups[bet.bet_id].status = "won";
      } else if (
        bet.status === "pending" &&
        groups[bet.bet_id].status !== "lost"
      ) {
        groups[bet.bet_id].status = "pending";
      }
    });

    return Object.values(groups).sort((a, b) => b.createdAt - a.createdAt);
  }, [betData.bets]);

  // Helper function to format date
  function formatDate(dateString) {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()} ${date
      .getHours()
      .toString()
      .padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
  }

  // Toggle expanding/collapsing a betslip
  const toggleExpand = (betId) => {
    setExpandedBets((prev) => ({
      ...prev,
      [betId]: !prev[betId],
    }));
  };

  // Filter and pagination logic
  const filteredData = useMemo(() => {
    return groupedBets;
  }, [groupedBets]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Pagination handler
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Always show first page
    pages.push(1);

    let startPage = Math.max(currentPage - 1, 2);
    let endPage = Math.min(currentPage + 1, totalPages - 1);

    if (currentPage <= 2) {
      endPage = 4;
    }
    if (currentPage >= totalPages - 1) {
      startPage = totalPages - 3;
    }

    if (startPage > 2) {
      pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages - 1) {
      pages.push("...");
    }

    // Always show last page
    pages.push(totalPages);

    return pages;
  };

  return (
    <div className="p-4 md:p-6 bg-teal min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-white text-2xl md:text-3xl font-bold mb-6">
          Bet History
        </h1>

        {/* Filters Section */}
        <div className="bg-forestGreen/80 rounded-t-xl shadow-dashboardshadow p-4 md:p-5">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex items-center gap-3">
                <label className="text-white text-sm font-medium">From:</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="bg-teal border border-doubleextralightwhite/30 rounded-md px-3 py-2 text-white focus:outline-none focus:border-yellow"
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-white text-sm font-medium">To:</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="bg-teal border border-doubleextralightwhite/30 rounded-md px-3 py-2 text-white focus:outline-none focus:border-yellow"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 relative">
              <label className="text-white text-sm font-medium">Type:</label>
              <div className="relative">
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="bg-teal appearance-none border border-doubleextralightwhite/30 rounded-md px-3 py-2 pr-10 min-w-[120px] text-white focus:outline-none focus:border-yellow"
                >
                  <option value="all">All</option>
                  <option value="sports">Sports</option>
                  <option value="casino">Casino</option>
                </select>
                <img
                  src={selectdropdownsvg}
                  alt="dropdown"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="bg-forestGreen shadow-dashboardshadow rounded-b-xl overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-white border-t-yellow"></div>
            </div>
          ) : currentData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="text-left border-b border-doubleextralightwhite/20">
                    <th className="p-4 text-xs md:text-sm font-medium text-doubleextralightwhite uppercase">
                      Ref ID
                    </th>
                    <th className="p-4 text-xs md:text-sm font-medium text-doubleextralightwhite uppercase">
                      Date/Time
                    </th>
                    <th className="p-4 text-xs md:text-sm font-medium text-doubleextralightwhite uppercase">
                      Stake
                    </th>
                    <th className="p-4 text-xs md:text-sm font-medium text-doubleextralightwhite uppercase">
                      Payout
                    </th>
                    <th className="p-4 text-xs md:text-sm font-medium text-doubleextralightwhite uppercase">
                      Status
                    </th>
                    <th className="p-4 text-xs md:text-sm font-medium text-doubleextralightwhite uppercase">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.map((betslip, index) => (
                    <React.Fragment key={betslip.bet_id}>
                      <tr
                        className={`hover:bg-forestGreen/60 ${
                          index % 2 === 0 ? "bg-forestGreen/30" : ""
                        }`}
                      >
                        <td className="p-4 text-white text-sm">
                          {betslip.bet_id.substring(0, 10)}...
                        </td>
                        <td className="p-4 text-white text-sm">
                          {betslip.formattedDate}
                        </td>
                        <td className="p-4 text-white text-sm">
                          {CURRENCY}
                          {betslip.totalStake.toFixed(2)}
                        </td>
                        <td className="p-4 text-white text-sm">
                          {CURRENCY}
                          {betslip.totalPotentialPayout.toFixed(2)}
                        </td>
                        <td className="p-4">
                          <span
                            className={`px-3 py-1 rounded-md text-xs font-medium border ${
                              STATUS_STYLES[betslip.status]?.bg
                            } ${STATUS_STYLES[betslip.status]?.text} ${
                              STATUS_STYLES[betslip.status]?.border
                            }`}
                          >
                            {betslip.status.charAt(0).toUpperCase() +
                              betslip.status.slice(1)}
                          </span>
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => toggleExpand(betslip.bet_id)}
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow/20 hover:bg-yellow/30 transition-colors"
                          >
                            <img
                              src={greater}
                              alt="expand"
                              className={`w-4 h-4 transition-transform ${
                                expandedBets[betslip.bet_id] ? "rotate-90" : ""
                              }`}
                            />
                          </button>
                        </td>
                      </tr>
                      {expandedBets[betslip.bet_id] && (
                        <tr>
                          <td colSpan="6" className="p-0">
                            <div className="p-4 border-t border-doubleextralightwhite/20 bg-teal/20">
                              <div className="flex justify-between items-center mb-3">
                                <h3 className="font-medium text-yellow text-sm uppercase">
                                  Bet Details
                                </h3>
                                <span
                                  className={`px-2 py-1 text-xs rounded ${
                                    STATUS_STYLES[betslip.status]?.bg
                                  } ${STATUS_STYLES[betslip.status]?.text}`}
                                >
                                  {betslip.bets.length} Selection
                                  {betslip.bets.length !== 1 ? "s" : ""}
                                </span>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full">
                                  <thead>
                                    <tr className="text-left border-b border-doubleextralightwhite/10">
                                      <th className="p-2 text-xs font-medium text-doubleextralightwhite">
                                        Match
                                      </th>
                                      <th className="p-2 text-xs font-medium text-doubleextralightwhite">
                                        Date
                                      </th>
                                      <th className="p-2 text-xs font-medium text-doubleextralightwhite">
                                        Selection
                                      </th>
                                      <th className="p-2 text-xs font-medium text-doubleextralightwhite">
                                        Market
                                      </th>
                                      <th className="p-2 text-xs font-medium text-doubleextralightwhite">
                                        Odds
                                      </th>
                                      <th className="p-2 text-xs font-medium text-doubleextralightwhite">
                                        Status
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {betslip.bets.map((bet) => (
                                      <tr
                                        key={bet.id}
                                        className="border-b border-doubleextralightwhite/10 last:border-0 hover:bg-teal/30"
                                      >
                                        <td className="p-2 text-xs text-white">
                                          Match #{bet.match_id}
                                        </td>
                                        <td className="p-2 text-xs text-white">
                                          {bet.event_date}
                                        </td>
                                        <td className="p-2 text-xs text-white">
                                          {bet.odd_label}
                                        </td>
                                        <td className="p-2 text-xs text-white">
                                          {bet.market_name}
                                        </td>
                                        <td className="p-2 text-xs text-yellow font-medium">
                                          {bet.odd_value}
                                        </td>
                                        <td className="p-2">
                                          <span
                                            className={`px-2 py-1 text-xs rounded-md ${
                                              STATUS_STYLES[bet.status]?.bg
                                            } ${
                                              STATUS_STYLES[bet.status]?.text
                                            }`}
                                          >
                                            {bet.status
                                              .charAt(0)
                                              .toUpperCase() +
                                              bet.status.slice(1)}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-10 text-center">
              <div className="mb-4 w-16 h-16 bg-yellow/20 rounded-full flex items-center justify-center">
                <span className="text-yellow text-2xl">!</span>
              </div>
              <h3 className="text-white font-medium mb-2">No Bets Found</h3>
              <p className="text-doubleextralightwhite text-sm max-w-md">
                No bet history found for the selected filters. Try changing your
                filters or place some bets!
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {currentData.length > 0 && (
          <div className="mt-6 flex justify-center">
            <div className="bg-forestGreen/80 rounded-lg shadow-dashboardshadow p-2 inline-flex gap-1 justify-center items-center">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded hover:bg-teal disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <img
                  src={greater}
                  alt="previous"
                  className="rotate-180 w-4 h-4 brightness-200"
                />
              </button>

              {getPageNumbers().map((page, index) => (
                <button
                  key={index}
                  onClick={() =>
                    typeof page === "number" && handlePageChange(page)
                  }
                  className={`w-8 h-8 flex items-center justify-center rounded-md text-sm
                    ${
                      typeof page === "number"
                        ? currentPage === page
                          ? "bg-yellow text-black"
                          : "text-white hover:bg-teal/50"
                        : "text-doubleextralightwhite"
                    }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded hover:bg-teal disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <img
                  src={greater}
                  alt="next"
                  className="w-4 h-4 brightness-200"
                />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BetHistory;
